import { FC, useEffect } from 'react';
import {
	Box,
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	TextField,
} from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useDispatch, useSelector } from 'react-redux';
import {
	createInvoicingFeeTypeRequest,
	resetSaveInvoicingFeeTypeSuccess,
	updateInvoicingFeeTypeRequest,
} from 'redux/reducers/easFee/reducer';
import {
	getInvoicingFeeTypeSaveLoading,
	getInvoicingFeeTypeSaveSuccess,
} from 'redux/reducers/easFee/selectors';
import { TInvoicingFeeTypeDialogProps } from './types';

const validationSchema = Yup.object({
	name: Yup.string().required('Name is required').max(255, 'Max 255 characters'),
	description: Yup.string().max(1000, 'Max 1000 characters'),
	feeValue: Yup.number().required('Fee Value is required').min(0, 'Must be >= 0'),
	invoiceThresholdEur: Yup.number()
		.required('Invoice Threshold is required')
		.min(0, 'Must be >= 0'),
});

export const InvoicingFeeTypeDialog: FC<TInvoicingFeeTypeDialogProps> = ({
	open,
	onClose,
	invoicingFeeTypeToEdit,
}) => {
	const dispatch = useDispatch();
	const isEdit = !!invoicingFeeTypeToEdit;

	const loading = useSelector(getInvoicingFeeTypeSaveLoading);
	const saveSuccess = useSelector(getInvoicingFeeTypeSaveSuccess);

	const formik = useFormik({
		initialValues: {
			name: invoicingFeeTypeToEdit?.name ?? '',
			description: invoicingFeeTypeToEdit?.description ?? '',
			feeValue: invoicingFeeTypeToEdit?.feeValue ?? ('' as unknown as number),
			invoiceThresholdEur: invoicingFeeTypeToEdit?.invoiceThresholdEur ?? ('' as unknown as number),
		},
		enableReinitialize: true,
		validationSchema,
		onSubmit: (values) => {
			const body = {
				name: values.name,
				description: values.description,
				feeValue: Number(values.feeValue),
				invoiceThresholdEur: Number(values.invoiceThresholdEur),
			};
			if (isEdit && invoicingFeeTypeToEdit) {
				dispatch(updateInvoicingFeeTypeRequest({ id: invoicingFeeTypeToEdit.id, body }));
			} else {
				dispatch(createInvoicingFeeTypeRequest(body));
			}
		},
	});

	useEffect(() => {
		if (saveSuccess) {
			dispatch(resetSaveInvoicingFeeTypeSuccess());
			formik.resetForm();
			onClose();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [saveSuccess]);

	const handleClose = () => {
		formik.resetForm();
		onClose();
	};

	return (
		<Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
			<DialogTitle>{isEdit ? 'Edit Invoicing Fee Type' : 'Add New Invoicing Fee Type'}</DialogTitle>
			<DialogContent>
				<Box
					component="form"
					onSubmit={formik.handleSubmit}
					sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}
				>
					<TextField
						label="Name"
						name="name"
						size="small"
						value={formik.values.name}
						onChange={formik.handleChange}
						onBlur={formik.handleBlur}
						error={formik.touched.name && !!formik.errors.name}
						helperText={formik.touched.name && formik.errors.name}
					/>
					<TextField
						label="Description"
						name="description"
						size="small"
						multiline
						minRows={2}
						value={formik.values.description}
						onChange={formik.handleChange}
						onBlur={formik.handleBlur}
						error={formik.touched.description && !!formik.errors.description}
						helperText={formik.touched.description && formik.errors.description}
					/>
					<TextField
						label="Fee Value"
						name="feeValue"
						type="number"
						size="small"
						inputProps={{ step: '0.01' }}
						value={formik.values.feeValue}
						onChange={formik.handleChange}
						onBlur={formik.handleBlur}
						error={formik.touched.feeValue && !!formik.errors.feeValue}
						helperText={formik.touched.feeValue && formik.errors.feeValue}
					/>
					<TextField
						label="Invoice Threshold (EUR)"
						name="invoiceThresholdEur"
						type="number"
						size="small"
						inputProps={{ step: '0.01' }}
						value={formik.values.invoiceThresholdEur}
						onChange={formik.handleChange}
						onBlur={formik.handleBlur}
						error={formik.touched.invoiceThresholdEur && !!formik.errors.invoiceThresholdEur}
						helperText={formik.touched.invoiceThresholdEur && formik.errors.invoiceThresholdEur}
					/>
				</Box>
			</DialogContent>
			<DialogActions>
				<Button onClick={handleClose} disabled={loading}>
					Cancel
				</Button>
				<Button
					onClick={() => formik.handleSubmit()}
					variant="contained"
					color="primary"
					disabled={loading}
					startIcon={loading ? <CircularProgress size={16} /> : undefined}
				>
					{isEdit ? 'Save' : 'Create'}
				</Button>
			</DialogActions>
		</Dialog>
	);
};
